import React, { useEffect, useState } from "react";
import classNames from "classnames";
import "./ScrollToTop.scss";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { useWindowSize } from "../../../hooks/window-hooks";
import { Container } from "../../Grid/Grid";
import { useServices } from "../../../hooks/services-hooks";
import { useTranslation } from "react-i18next";

/**
 * Props for the ScrollToTop component.
 *
 * @description The ScrollToTop component is a floating button that appears when
 * the user scrolls down the page, allowing them to quickly return to the top.
 *
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic usage
 * <ScrollToTop />
 *
 * @example
 * // With test ID
 * <ScrollToTop testId="scroll-to-top-btn" />
 */
interface Props {
  testId?: string;
}

/**
 * A floating button that scrolls the page to the top.
 *
 * @description ScrollToTop renders a floating button that appears when the user
 * scrolls down the page. When clicked, it smoothly scrolls the page back to the top.
 * The button automatically hides when near the top and adjusts its position when
 * the footer is reached.
 *
 * Features:
 * - Appears after scrolling past half the viewport height
 * - Smooth scroll animation to top
 * - Position adjustment when footer is reached
 * - Mobile and desktop scroll handling
 * - Services selection state awareness for positioning
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A floating scroll-to-top button
 *
 * @see Props for all available properties
 */
export const ScrollToTop: React.FC<Props> = ({ testId }) => {
  const { t } = useTranslation();
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [isFooterReached, setFooterReached] = useState(false);
  const { isMobile } = useWindowSize();
  const { selectedServices } = useServices();

  const getMainBodyElement = (): HTMLElement | null => {
    return document.getElementById("main-body");
  };
  const wrapClass = classNames("scroll", {
    open: !!showTopBtn,
    absolute: isFooterReached,
    "services-selected": selectedServices.length > 0 && !isFooterReached,
    "services-selected--footer-reached":
      selectedServices.length > 0 && isFooterReached,
  });

  useEffect(() => {
    const mainBodyElement = getMainBodyElement();
    if (!mainBodyElement) {
      return () => {};
    }

    const onScroll = () => {
      const pixels = isMobile ? 201 : 124;

      if (mainBodyElement.scrollTop > mainBodyElement.clientHeight / 2) {
        setShowTopBtn(true);
      } else {
        setShowTopBtn(false);
      }

      if (
        mainBodyElement.scrollTop + mainBodyElement.clientHeight + pixels >=
        mainBodyElement.scrollHeight
      ) {
        setFooterReached(true);
      } else {
        setFooterReached(false);
      }
    };

    const onScrollMobile = () => {
      if (window.scrollY > document.body.scrollHeight / 2) {
        setShowTopBtn(true);
      } else {
        setShowTopBtn(false);
      }

      if (
        window.scrollY + window.innerHeight + 149 >=
        document.body.scrollHeight
      ) {
        setFooterReached(true);
      } else {
        setFooterReached(false);
      }
    };

    mainBodyElement.addEventListener("scroll", onScroll);

    if (!isMobile) {
      mainBodyElement.addEventListener("scroll", onScroll);
    } else {
      window.addEventListener("scroll", onScrollMobile);
    }

    return () => {
      mainBodyElement.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScrollMobile);
    };
  }, [isMobile]);

  const handleOnClick = () => {
    const mainBodyElement = getMainBodyElement();
    if (mainBodyElement) {
      mainBodyElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <Container className="scroll-container">
      <div
        className="scroll-wrapper"
        data-testid={testId}
        title={t("PRIVACY_BACK_TO_TOP")}
      >
        <div className={wrapClass}>
          <button
            className="scroll__button"
            onClick={handleOnClick}
            aria-label="scroll-to-top-btn"
          >
            <Svg icon={SVG_ICONS.ARROW_UP} width={24} height={24} />
          </button>
        </div>
      </div>
    </Container>
  );
};
